/**
 * Currency utilities and country-to-currency mapping
 *
 * This module provides centralized currency management based on user's country.
 * All monetary values in the application should use the currency derived from
 * the user's profile country setting.
 */

// ============================================================================
// Types
// ============================================================================

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  locale: string;
  decimals: number;
}

export interface CountryCurrencyMapping {
  [countryName: string]: CurrencyInfo;
}

// ============================================================================
// Currency Definitions
// ============================================================================

export const CURRENCIES = {
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    locale: 'es-ES', // Default to Spanish locale for EUR
    decimals: 2,
  },
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    locale: 'en-US',
    decimals: 2,
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound',
    locale: 'en-GB',
    decimals: 2,
  },
  CHF: {
    code: 'CHF',
    symbol: 'CHF',
    name: 'Swiss Franc',
    locale: 'de-CH',
    decimals: 2,
  },
  MXN: {
    code: 'MXN',
    symbol: '$',
    name: 'Mexican Peso',
    locale: 'es-MX',
    decimals: 2,
  },
  CAD: {
    code: 'CAD',
    symbol: 'C$',
    name: 'Canadian Dollar',
    locale: 'en-CA',
    decimals: 2,
  },
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    name: 'Australian Dollar',
    locale: 'en-AU',
    decimals: 2,
  },
  JPY: {
    code: 'JPY',
    symbol: '¥',
    name: 'Japanese Yen',
    locale: 'ja-JP',
    decimals: 0,
  },
  // Additional currencies for global support
  ARS: {
    code: 'ARS',
    symbol: '$',
    name: 'Argentine Peso',
    locale: 'es-AR',
    decimals: 2,
  },
  BRL: {
    code: 'BRL',
    symbol: 'R$',
    name: 'Brazilian Real',
    locale: 'pt-BR',
    decimals: 2,
  },
  COP: {
    code: 'COP',
    symbol: '$',
    name: 'Colombian Peso',
    locale: 'es-CO',
    decimals: 2,
  },
  CLP: {
    code: 'CLP',
    symbol: '$',
    name: 'Chilean Peso',
    locale: 'es-CL',
    decimals: 0,
  },
  PEN: {
    code: 'PEN',
    symbol: 'S/',
    name: 'Peruvian Sol',
    locale: 'es-PE',
    decimals: 2,
  },
  CNY: {
    code: 'CNY',
    symbol: '¥',
    name: 'Chinese Yuan',
    locale: 'zh-CN',
    decimals: 2,
  },
  INR: {
    code: 'INR',
    symbol: '₹',
    name: 'Indian Rupee',
    locale: 'en-IN',
    decimals: 2,
  },
  KRW: {
    code: 'KRW',
    symbol: '₩',
    name: 'South Korean Won',
    locale: 'ko-KR',
    decimals: 0,
  },
  NZD: {
    code: 'NZD',
    symbol: 'NZ$',
    name: 'New Zealand Dollar',
    locale: 'en-NZ',
    decimals: 2,
  },
  ZAR: {
    code: 'ZAR',
    symbol: 'R',
    name: 'South African Rand',
    locale: 'en-ZA',
    decimals: 2,
  },
  MAD: {
    code: 'MAD',
    symbol: 'DH',
    name: 'Moroccan Dirham',
    locale: 'ar-MA',
    decimals: 2,
  },
  NOK: {
    code: 'NOK',
    symbol: 'kr',
    name: 'Norwegian Krone',
    locale: 'no-NO',
    decimals: 2,
  },
  SEK: {
    code: 'SEK',
    symbol: 'kr',
    name: 'Swedish Krona',
    locale: 'sv-SE',
    decimals: 2,
  },
  DKK: {
    code: 'DKK',
    symbol: 'kr',
    name: 'Danish Krone',
    locale: 'da-DK',
    decimals: 2,
  },
  PLN: {
    code: 'PLN',
    symbol: 'zł',
    name: 'Polish Zloty',
    locale: 'pl-PL',
    decimals: 2,
  },
} as const;

// ============================================================================
// Country-to-Currency Mapping
// ============================================================================

/**
 * Maps country names (as used in profile setup) to their primary currency.
 * This mapping drives the automatic currency assignment based on user's country.
 *
 * @deprecated This mapping by country name is being phased out in favor of
 * country code-based mapping. Use getCurrencyForCountryCode from countries-cities.ts instead.
 */
export const COUNTRY_CURRENCY_MAP: CountryCurrencyMapping = {
  // Europe
  España: CURRENCIES.EUR,
  Francia: CURRENCIES.EUR,
  Italia: CURRENCIES.EUR,
  Portugal: CURRENCIES.EUR,
  'Reino Unido': CURRENCIES.GBP,
  Alemania: CURRENCIES.EUR,
  'Países Bajos': CURRENCIES.EUR,
  Bélgica: CURRENCIES.EUR,
  Suiza: CURRENCIES.CHF,
  Austria: CURRENCIES.EUR,
  Irlanda: CURRENCIES.EUR,
  Noruega: CURRENCIES.NOK,
  Suecia: CURRENCIES.SEK,
  Dinamarca: CURRENCIES.DKK,
  Finlandia: CURRENCIES.EUR,
  Polonia: CURRENCIES.PLN,

  // Americas
  'Estados Unidos': CURRENCIES.USD,
  México: CURRENCIES.MXN,
  Canadá: CURRENCIES.CAD,
  Australia: CURRENCIES.AUD,
  Argentina: CURRENCIES.ARS,
  Brasil: CURRENCIES.BRL,
  Colombia: CURRENCIES.COP,
  Chile: CURRENCIES.CLP,
  Perú: CURRENCIES.PEN,

  // Asia
  Japón: CURRENCIES.JPY,
  China: CURRENCIES.CNY,
  India: CURRENCIES.INR,
  'Corea del Sur': CURRENCIES.KRW,

  // Oceania
  'Nueva Zelanda': CURRENCIES.NZD,

  // Africa
  Sudáfrica: CURRENCIES.ZAR,
  Marruecos: CURRENCIES.MAD,

  // Legacy mapping for backwards compatibility
  Luxemburgo: CURRENCIES.EUR,
  Grecia: CURRENCIES.EUR,
  Eslovenia: CURRENCIES.EUR,
  Eslovaquia: CURRENCIES.EUR,
  Estonia: CURRENCIES.EUR,
  Letonia: CURRENCIES.EUR,
  Lituania: CURRENCIES.EUR,
  Malta: CURRENCIES.EUR,
  Chipre: CURRENCIES.EUR,
} as const;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get currency information for a given country
 * @param countryName - Country name as stored in user profile
 * @returns Currency information or default EUR if country not found
 */
export function getCurrencyForCountry(countryName: string): CurrencyInfo {
  const currency = COUNTRY_CURRENCY_MAP[countryName];
  if (!currency) {
    console.warn(
      `⚠️ No currency mapping found for country: ${countryName}, defaulting to EUR`,
    );
    return CURRENCIES.EUR;
  }
  return currency;
}

/**
 * Get currency code for a given country
 * @param countryName - Country name as stored in user profile
 * @returns Currency code (e.g., 'EUR', 'USD')
 */
export function getCurrencyCodeForCountry(countryName: string): string {
  return getCurrencyForCountry(countryName).code;
}

/**
 * Get currency symbol for a given country
 * @param countryName - Country name as stored in user profile
 * @returns Currency symbol (e.g., '€', '$')
 */
export function getCurrencySymbolForCountry(countryName: string): string {
  return getCurrencyForCountry(countryName).symbol;
}

/**
 * Get locale string for a given country's currency formatting
 * @param countryName - Country name as stored in user profile
 * @returns Locale string (e.g., 'es-ES', 'en-US')
 */
export function getLocaleForCountry(countryName: string): string {
  return getCurrencyForCountry(countryName).locale;
}

/**
 * Check if a currency code is valid/supported
 * @param currencyCode - Currency code to validate
 * @returns True if currency is supported
 */
export function isValidCurrency(currencyCode: string): boolean {
  return Object.keys(CURRENCIES).includes(currencyCode.toUpperCase());
}

/**
 * Get all supported currency codes
 * @returns Array of supported currency codes
 */
export function getSupportedCurrencies(): string[] {
  return Object.keys(CURRENCIES);
}

/**
 * Get currency info by currency code
 * @param currencyCode - Currency code (e.g., 'EUR', 'USD')
 * @returns Currency information or undefined if not found
 */
export function getCurrencyInfo(
  currencyCode: string,
): CurrencyInfo | undefined {
  return CURRENCIES[currencyCode.toUpperCase() as keyof typeof CURRENCIES];
}

// ============================================================================
// Formatting Functions
// ============================================================================

/**
 * Format currency amount using country-based currency settings
 * @param amount - Amount to format
 * @param countryName - Country name to determine currency
 * @returns Formatted currency string
 */
export function formatCurrencyForCountry(
  amount: number,
  countryName: string,
): string {
  const currency = getCurrencyForCountry(countryName);
  const safeAmount =
    typeof amount === 'number' && isFinite(amount) ? amount : 0;

  return safeAmount.toLocaleString(currency.locale, {
    style: 'currency',
    currency: currency.code,
    minimumFractionDigits: currency.decimals,
    maximumFractionDigits: currency.decimals,
  });
}

/**
 * Format currency amount with custom symbol (backward compatibility)
 * @param amount - Amount to format
 * @param countryName - Country name to determine currency
 * @returns Formatted currency string with symbol
 */
export function formatCurrencyWithSymbolForCountry(
  amount: number,
  countryName: string,
): string {
  const currency = getCurrencyForCountry(countryName);
  const safeAmount =
    typeof amount === 'number' && isFinite(amount) ? amount : 0;

  // Format number with appropriate decimals
  const formattedNumber = safeAmount.toLocaleString(currency.locale, {
    minimumFractionDigits: currency.decimals,
    maximumFractionDigits: currency.decimals,
  });

  return `${formattedNumber} ${currency.symbol}`;
}

// ============================================================================
// Default Values
// ============================================================================

/**
 * Default currency (EUR) for fallback scenarios
 */
export const DEFAULT_CURRENCY = CURRENCIES.EUR;

/**
 * Default country (España) for fallback scenarios
 */
export const DEFAULT_COUNTRY = 'España';
